import React from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { Home, Compass, Bell, User, LogOut, Settings, LogIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { StatusBarSpacer } from '../components/StatusBarSpacer';
import { useUnreadNotificationsCount } from '../hooks/useNotifications';

const tabs = [
  { to: '/', label: 'Feed', icon: Home, showBadge: false, requiresAuth: false },
  { to: '/discover', label: 'Discover', icon: Compass, showBadge: false, requiresAuth: false },
  { to: '/notifications', label: 'Notifications', icon: Bell, showBadge: true, requiresAuth: true },
  { to: '/profile', label: 'Profile', icon: User, showBadge: false, requiresAuth: true },
];

export function SocialWallLayout() {
  const { user, profile, signOut } = useAuth();
  const { data: unreadCount } = useUnreadNotificationsCount();
  const isAuthenticated = !!user;

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getAvatarUrl = () => {
    return profile?.avatar_url || user?.user_metadata?.avatar_url;
  };

  return (
    <div 
      className="flex flex-col h-screen bg-background"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
      }}
    >
      {/* Status bar spacer for native apps */}
      <StatusBarSpacer />

      {/* Top Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-heading font-bold uppercase text-primary">RecruitLook Social</h1>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="bg-secondary/20 text-secondary text-[10px] font-heading font-bold uppercase px-1.5 py-0.5 rounded cursor-help">
                  Beta
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">This feature is new and currently in testing. We'd love your feedback!</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus:outline-none">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={getAvatarUrl()} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="flex items-center gap-2 text-destructive"
                onClick={() => signOut()}
              >
                <LogOut className="h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="default" size="sm" asChild>
            <Link to="/login" className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              Sign In
            </Link>
          </Button>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Outlet />
      </main>

      {/* Bottom Tab Navigation with safe area support */}
      <nav 
        className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around items-center h-16">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const showNotificationBadge = tab.showBadge && unreadCount && unreadCount > 0 && isAuthenticated;
            
            // For auth-required tabs, link to login if not authenticated
            const linkTo = tab.requiresAuth && !isAuthenticated ? '/login' : tab.to;
            
            return (
              <NavLink
                key={tab.to}
                to={linkTo}
                end={tab.to === '/'}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center justify-center flex-1 h-full transition-colors relative",
                    isActive && !tab.requiresAuth
                      ? "text-primary" 
                      : "text-muted-foreground hover:text-foreground"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <div className="relative">
                      <Icon size={24} strokeWidth={isActive && !tab.requiresAuth ? 2.5 : 2} />
                      {showNotificationBadge && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <span className="text-xs mt-1 font-medium">{tab.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
